import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  LinearProgress,
  Container,
  TablePagination,
  Pagination,
  Stack,
} from '@mui/material';
import {
  Search,
  Warning,
  Inventory as InventoryIcon,
  Refresh,
} from '@mui/icons-material';
import { inventoryAPI } from '../../services/API';
import { useSnackbar } from 'notistack';

export const Inventory = () => {
  const [stockSummary, setStockSummary] = useState([]);
  const [expiryAlerts, setExpiryAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [lowStockFilter, setLowStockFilter] = useState(false);

  // Pagination states for stock summary
  const [stockPage, setStockPage] = useState(0);
  const [stockRowsPerPage, setStockRowsPerPage] = useState(10);
  const [totalStockCount, setTotalStockCount] = useState(0);

  // Pagination states for expiry alerts
  const [alertsPage, setAlertsPage] = useState(0);
  const [alertsRowsPerPage, setAlertsRowsPerPage] = useState(6);
  const [totalAlertsCount, setTotalAlertsCount] = useState(0);

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    fetchInventoryData();
  }, []);

  const fetchInventoryData = async () => {
    try {
      const [summaryResponse, alertsResponse] = await Promise.all([
        inventoryAPI.getStockSummary({ 
          lowStockOnly: 'false',
          page: stockPage + 1, // Send current page to backend
          limit: stockRowsPerPage
        }),
        inventoryAPI.getExpiryAlerts({
          page: alertsPage + 1, // Send current page to backend
          limit: alertsRowsPerPage
        }),
      ]);

      setStockSummary(summaryResponse.data.data.stockSummary);
      setExpiryAlerts(alertsResponse.data.data.alerts);
      setTotalStockCount(summaryResponse.data.data.pagination.totalItems); // Use backend count
      setTotalAlertsCount(alertsResponse.data.data.pagination.totalItems); // Use backend count
    } catch (error) {
      enqueueSnackbar('Error fetching inventory data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Handle stock table pagination
  const handleStockChangePage = (event, newPage) => {
    setStockPage(newPage);
    // Refetch data with new pagination
    fetchInventoryData();
  };

  const handleStockChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setStockRowsPerPage(newRowsPerPage);
    setStockPage(0);
    // Refetch data with new rows per page
    fetchInventoryData();
  };

  // Handle alerts pagination
  const handleAlertsPageChange = (event, newPage) => {
    setAlertsPage(newPage - 1); // Pagination component uses 1-based indexing
    // Refetch data with new pagination
    fetchInventoryData();
  };

  // Handle search with debouncing (optional improvement)
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setStockPage(0); // Reset to first page when searching
  };

  // Filter stock data based on search and filters (client-side filtering for now)
  const filteredStock = stockSummary
    .filter(
      (item) =>
        item.medicine.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.medicine.genericName.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter((item) => !lowStockFilter || item.lowStock);

  // Total pages for alerts from backend
  const totalAlertsPages = Math.ceil(totalAlertsCount / alertsRowsPerPage);

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)',
        fontSize: '16px',
      }}
    >
      <Container maxWidth="xl" sx={{ py: 5 }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 4,
          }}
        >
          <Typography variant="h3" fontWeight="bold" sx={{ fontSize: { xs: '2rem', md: '2.5rem' } }}>
            Inventory Management
          </Typography>
          <Button
            startIcon={<Refresh />}
            onClick={fetchInventoryData}
            variant="outlined"
            sx={{
              borderRadius: 2,
              fontSize: '1.1rem',
              px: 3,
              py: 1,
              transition: '0.3s',
              '&:hover': {
                backgroundColor: 'primary.main',
                color: '#fff',
                transform: 'scale(1.03)',
              },
            }}
          >
            Refresh
          </Button>
        </Box>

        {/* Alerts Summary */}
        {expiryAlerts.length > 0 && (
          <Alert
            severity="warning"
            sx={{
              mb: 3,
              borderRadius: 2,
              boxShadow: 2,
              backgroundColor: '#fff8e1',
              fontSize: '1.1rem',
              py: 2,
              '& .MuiAlert-message': {
                fontSize: '1.1rem',
                fontWeight: 500,
              }
            }}
          >
            {totalAlertsCount} medicines expiring soon
          </Alert>
        )}

        {/* Summary Cards */}
        <Grid container spacing={3}>
          {[
            {
              label: 'Total Medicines',
              value: totalStockCount,
              color: 'primary.main',
              icon: <InventoryIcon sx={{ fontSize: 50 }} color="primary" />,
            },
            {
              label: 'Low Stock Items',
              value: stockSummary.filter((i) => i.lowStock).length,
              color: 'warning.main',
              icon: <Warning sx={{ fontSize: 50 }} color="warning" />,
            },
            {
              label: 'Critical Expiry',
              value: expiryAlerts.filter((a) => a.severity === 'critical').length,
              color: 'error.main',
              icon: <Warning sx={{ fontSize: 50 }} color="error" />,
            },
            {
              label: 'Adequate Stock',
              value: stockSummary.filter((i) => !i.lowStock).length,
              color: 'success.main',
              icon: <InventoryIcon sx={{ fontSize: 50 }} color="success" />,
            },
          ].map((card, idx) => (
            <Grid item xs={12} sm={6} md={3} key={idx}>
              <Card
                sx={{
                  height: '100%',
                  textAlign: 'center',
                  borderRadius: 3,
                  boxShadow: 3,
                  transition: '0.3s',
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  {card.icon}
                  <Typography
                    variant="h2"
                    fontWeight="bold"
                    sx={{ 
                      color: card.color, 
                      mt: 2,
                      fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }
                    }}
                  >
                    {card.value}
                  </Typography>
                  <Typography 
                    variant="h6"
                    color="text.secondary"
                    sx={{ fontSize: { xs: '1rem', md: '1.2rem' }, mt: 1 }}
                  >
                    {card.label}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Filters */}
        <Card
          sx={{
            mt: 4,
            mb: 3,
            borderRadius: 3,
            boxShadow: 2,
            transition: '0.3s',
            '&:hover': { boxShadow: 4 },
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search medicines..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ fontSize: '1.5rem' }} color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiInputBase-input': {
                      fontSize: '1.1rem',
                      py: 2,
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '1.1rem',
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Button
                  variant={lowStockFilter ? 'contained' : 'outlined'}
                  onClick={() => {
                    setLowStockFilter(!lowStockFilter);
                    setStockPage(0);
                  }}
                  color={lowStockFilter ? 'warning' : 'primary'}
                  fullWidth
                  sx={{
                    borderRadius: 2,
                    height: 56,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    transition: '0.3s',
                    '&:hover': { transform: 'scale(1.02)' },
                  }}
                >
                  Show Only Low Stock
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Inventory Table */}
        <Card sx={{ borderRadius: 3, boxShadow: 3, mb: 4 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.3rem', md: '1.5rem' } }}>
                Medicine Stock Summary
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '0.9rem', md: '1.1rem' } }}>
                Showing {stockSummary.length} of {totalStockCount} medicines
              </Typography>
            </Box>

            <TableContainer component={Paper} variant="outlined">
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: { xs: '0.9rem', md: '1.1rem' }, fontWeight: 600, py: 2 }}>Medicine</TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.9rem', md: '1.1rem' }, fontWeight: 600, py: 2 }}>Generic Name</TableCell>
                    <TableCell align="center" sx={{ fontSize: { xs: '0.9rem', md: '1.1rem' }, fontWeight: 600, py: 2 }}>Total Stock</TableCell>
                    <TableCell align="center" sx={{ fontSize: { xs: '0.9rem', md: '1.1rem' }, fontWeight: 600, py: 2 }}>Batches</TableCell>
                    <TableCell align="center" sx={{ fontSize: { xs: '0.9rem', md: '1.1rem' }, fontWeight: 600, py: 2 }}>Status</TableCell>
                    <TableCell align="center" sx={{ fontSize: { xs: '0.9rem', md: '1.1rem' }, fontWeight: 600, py: 2 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stockSummary.map((item) => (
                    <TableRow key={item.medicine._id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell sx={{ py: 2.5 }}>
                        <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', md: '1.1rem' } }}>
                          {item.medicine.name}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', md: '1rem' } }}>
                          {item.medicine.brand}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.9rem', md: '1.1rem' }, py: 2.5 }}>{item.medicine.genericName}</TableCell>
                      <TableCell align="center" sx={{ py: 2.5 }}>
                        <Typography
                          variant="h4"
                          fontWeight="bold"
                          sx={{
                            fontSize: { xs: '1.2rem', md: '1.5rem' },
                            color: item.lowStock ? 'warning.main' : 'success.main'
                          }}
                        >
                          {item.totalStock}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ py: 2.5 }}>
                        <Chip
                          label={item.batches.length}
                          size="medium"
                          variant="outlined"
                          sx={{ fontSize: { xs: '0.8rem', md: '1rem' }, height: 32 }}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ py: 2.5 }}>
                        <Chip
                          label={item.lowStock ? 'Low Stock' : 'Adequate'}
                          color={item.lowStock ? 'warning' : 'success'}
                          size="medium"
                          sx={{ fontSize: { xs: '0.8rem', md: '1rem' }, fontWeight: 600, height: 32 }}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ py: 2.5 }}>
                        <Button
                          size="large"
                          variant="outlined"
                          onClick={() =>
                            window.open(`/medicines/${item.medicine._id}`, '_blank')
                          }
                          sx={{
                            borderRadius: 2,
                            fontSize: { xs: '0.8rem', md: '1rem' },
                            px: 3,
                            transition: '0.3s',
                            '&:hover': {
                              backgroundColor: 'primary.main',
                              color: '#fff',
                            },
                          }}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {stockSummary.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="h5" color="text.secondary" sx={{ fontSize: { xs: '1.1rem', md: '1.3rem' } }}>
                  No medicines found matching your criteria
                </Typography>
              </Box>
            )}

            {/* Stock Table Pagination */}
            {totalStockCount > 0 && (
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={totalStockCount}
                rowsPerPage={stockRowsPerPage}
                page={stockPage}
                onPageChange={handleStockChangePage}
                onRowsPerPageChange={handleStockChangeRowsPerPage}
                sx={{ 
                  borderTop: '1px solid', 
                  borderColor: 'divider',
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontSize: { xs: '0.8rem', md: '1rem' }
                  }
                }}
              />
            )}
          </CardContent>
        </Card>

        {/* Expiry Alerts with Pagination */}
        {expiryAlerts.length > 0 && (
          <Card
            sx={{
              mt: 3,
              borderRadius: 3,
              boxShadow: 3,
              backgroundColor: '#fffdf5',
              transition: '0.3s',
              '&:hover': { boxShadow: 5 },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" fontWeight="bold" color="warning.main" sx={{ fontSize: { xs: '1.3rem', md: '1.5rem' } }}>
                  ⚠️ Expiry Alerts ({totalAlertsCount})
                </Typography>
                {totalAlertsPages > 1 && (
                  <Stack spacing={2} alignItems="center">
                    <Pagination
                      count={totalAlertsPages}
                      page={alertsPage + 1}
                      onChange={handleAlertsPageChange}
                      color="primary"
                      size="large"
                    />
                  </Stack>
                )}
              </Box>

              <Grid container spacing={2}>
                {expiryAlerts.map((alert) => (
                  <Grid item xs={12} sm={6} md={4} key={alert._id}>
                    <Alert
                      severity={
                        alert.severity === 'critical'
                          ? 'error'
                          : alert.severity === 'high'
                          ? 'warning'
                          : 'info'
                      }
                      icon={false}
                      sx={{
                        borderRadius: 2,
                        transition: '0.3s',
                        p: 2,
                        '&:hover': { transform: 'scale(1.02)' },
                        '& .MuiAlert-message': {
                          width: '100%'
                        }
                      }}
                    >
                      <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '1rem', md: '1.2rem' }, mb: 1 }}>
                        {alert.medicine.name}
                      </Typography>
                      <Typography variant="body1" sx={{ fontSize: { xs: '0.9rem', md: '1.1rem' }, mb: 0.5 }}>
                        Batch: {alert.batchNumber}
                      </Typography>
                      <Typography variant="body1" sx={{ fontSize: { xs: '0.9rem', md: '1.1rem' }, mb: 0.5 }}>
                        Expires in {alert.daysUntilExpiry} days
                      </Typography>
                      <Typography variant="body1" sx={{ fontSize: { xs: '0.9rem', md: '1.1rem' }, fontWeight: 600 }}>
                        Stock: {alert.availableQuantity}
                      </Typography>
                    </Alert>
                  </Grid>
                ))}
              </Grid>

              {/* Alerts Pagination Info */}
              {totalAlertsCount > alertsRowsPerPage && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
                  <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '0.9rem', md: '1.1rem' } }}>
                    Showing {alertsPage * alertsRowsPerPage + 1} - {Math.min((alertsPage + 1) * alertsRowsPerPage, totalAlertsCount)} of {totalAlertsCount} alerts
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="large"
                      disabled={alertsPage === 0}
                      onClick={() => setAlertsPage(alertsPage - 1)}
                      sx={{ fontSize: { xs: '0.8rem', md: '1rem' } }}
                    >
                      Previous
                    </Button>
                    <Button
                      size="large"
                      disabled={(alertsPage + 1) * alertsRowsPerPage >= totalAlertsCount}
                      onClick={() => setAlertsPage(alertsPage + 1)}
                      sx={{ fontSize: { xs: '0.8rem', md: '1rem' } }}
                    >
                      Next
                    </Button>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        )}
      </Container>
    </Box>
  );
};